import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import TextInput from "~/assets/js/components/Forms/TextInput";
import DropdownSelect from "~/assets/js/components/Forms/DropdownSelect";
import i18n from "~/assets/js/Helpers/i18n";
import { updateLocation } from "~/assets/js/Api/storeApi";
import useAreaCookie from "~/assets/js/Helpers/hooks/useAreaCookie";
import { legalFormOptions } from "./legalFormOptions";
import AddressPopup from "~/assets/js/components/AddressPopup";
import useGroupOrder from "~/assets/js/global/useGroupOrder";

/**
 * This component represents the address information fields for Contact Details
 * and is not meant to be used as a standalone form
 */
const AddressInfoFields = ({
    addressFields,
    addressErrors,
    addressTouched,
    formik,
    prefix,
}) => {
    const withPrefix = (fieldName) => `${prefix}.${fieldName}`;
    const cookie = useAreaCookie();
    const isDelivery = cookie.area.type === "delivery";
    const isDeliveryAddress = prefix === "deliveryAddress";
    const isCompanyOrder = formik.values.is_company;

    const { isGroupOrder } = useGroupOrder();

    const [overlayOpen, setOverlayOpen] = useState(cookieExists);

    const openDeliverySelector = isDelivery && isDeliveryAddress;
    const cookieExists =
        cookie.area === undefined ||
        window.location.href.substr(window.location.href.length - 13) ===
            "change-area=1";

    const handleManualFormikUpdate = (field, value) =>
        formik.setFieldValue(withPrefix(field), value);

    const submitData = async (
        area_update,
        location_update,
        address_update,
        shouldCloseOverlay = true
    ) => {
        const data = {
            area: area_update,
            location: location_update,
            address: address_update,
        };

        window.cookie.update(data);
        window.dispatchEvent(
            new CustomEvent("cookie-update", {
                bubbles: false,
                detail: data,
            })
        );

        shouldCloseOverlay && setOverlayOpen(false);

        const success = await updateLocation();

        if (!success && context !== undefined) {
            return context.dispatch(
                AddToast("danger", i18n.t("toasts.locationUpdate.error"))
            );
        }

        return updateFormikFormValues(data.address);
    };

    const updateFormikFormValues = (address) => {
        handleManualFormikUpdate(
            "address",
            [address.address, address.housenumber].join(" ")
        );
        handleManualFormikUpdate("postcode", address.postcode);
        handleManualFormikUpdate("city", address.locality);
    };

    useEffect(() => {
        if (cookie && cookie.address && isDeliveryAddress) {
            updateFormikFormValues(cookie.address);
        }
    }, []);

    return (
        <>
            {overlayOpen && (
                <AddressPopup
                    setOpen={setOverlayOpen}
                    submitData={submitData}
                />
            )}
            <TextInput
                bindName={withPrefix("first_name")}
                onChange={formik.handleChange}
                value={addressFields.first_name}
                label={i18n.t("form.label.firstName")}
                onBlur={formik.handleBlur}
                error={
                    !!addressErrors?.first_name && !!addressTouched?.first_name
                }
                errorMessage={i18n.t(addressErrors?.first_name)}
            />
            <TextInput
                bindName={withPrefix("last_name")}
                onChange={formik.handleChange}
                value={addressFields.last_name}
                label={i18n.t("form.label.lastName")}
                onBlur={formik.handleBlur}
                error={
                    !!addressErrors?.last_name && !!addressTouched?.last_name
                }
                errorMessage={i18n.t(addressErrors?.last_name)}
            />
            <div className={"pottsalat-form__flex-row"}>
            <TextInput
                bindName={withPrefix("company")}
                onChange={formik.handleChange}
                value={addressFields.company}
                label={
                    isCompanyOrder
                        ? i18n.t("form.label.companyName")
                        : i18n.t("form.label.companyName") +
                          i18n.t("form.label.optional")
                }
                onBlur={formik.handleBlur}
                error={!!addressErrors?.company && !!addressTouched?.company}
                errorMessage={i18n.t(addressErrors?.company)}
            />

            {isCompanyOrder && (
                <DropdownSelect
                    label={i18n.t("form.label.legalForm")}
                    options={legalFormOptions}
                    onChange={(value) =>
                        handleManualFormikUpdate("legal_form", value)
                    }
                    value={addressFields.legal_form}
                    bindName={withPrefix("legal_form")}
                    placeholder={i18n.t("form.label.pleaseSelect")}
                    translationKey={"legalFormTypes"}
                    error={
                        !!addressErrors?.legal_form &&
                        !!addressTouched?.legal_form
                    }
                    errorMessage={i18n.t(addressErrors?.legal_form)}
                />
            )}
            </div>

            {openDeliverySelector && (
                <>
                    <TextInput
                        bindName={withPrefix("address")}
                        value={
                            cookie.address
                                ? `${cookie.address.address} ${cookie.address.housenumber}`
                                : addressFields.address
                        }
                        label={i18n.t("form.label.address")}
                        onBlur={formik.handleBlur}
                        onFocus={() => setOverlayOpen(true)}
                        error={
                            !!addressErrors?.address &&
                            !!addressTouched?.address
                        }
                        errorMessage={i18n.t(addressErrors?.address)}
                        autocomplete="off"
                        disabled={isGroupOrder}
                    />
                    <div className="pottsalat-form__same-row">
                        <TextInput
                            bindName={withPrefix("postcode")}
                            value={
                                cookie.address
                                    ? cookie.address.postcode
                                    : addressFields.postcode
                            }
                            label={i18n.t("form.label.postcode")}
                            onBlur={formik.handleBlur}
                            onFocus={() => setOverlayOpen(true)}
                            error={
                                !!addressErrors?.postcode &&
                                !!addressTouched?.postcode
                            }
                            errorMessage={i18n.t(addressErrors?.postcode)}
                            autocomplete="off"
                            disabled={isGroupOrder}
                        />
                        <TextInput
                            bindName={withPrefix("city")}
                            value={
                                cookie.address
                                    ? cookie.address.locality
                                    : addressFields.city
                            }
                            label={i18n.t("form.label.city")}
                            onBlur={formik.handleBlur}
                            onFocus={() => setOverlayOpen(true)}
                            error={
                                !!addressErrors?.city && !!addressTouched?.city
                            }
                            errorMessage={i18n.t(addressErrors?.city)}
                            disabled={isGroupOrder}
                            autocomplete="off"
                        />
                    </div>
                </>
            )}
            {!openDeliverySelector && (
                <>
                    <TextInput
                        bindName={withPrefix("address")}
                        onChange={formik.handleChange}
                        value={addressFields.address}
                        label={i18n.t("form.label.address")}
                        onBlur={formik.handleBlur}
                        error={
                            !!addressErrors?.address &&
                            !!addressTouched?.address
                        }
                        errorMessage={i18n.t(addressErrors?.address)}
                    />
                    <div className="pottsalat-form__same-row">
                        <TextInput
                            bindName={withPrefix("postcode")}
                            onChange={formik.handleChange}
                            value={addressFields.postcode}
                            label={i18n.t("form.label.postcode")}
                            onBlur={formik.handleBlur}
                            error={
                                !!addressErrors?.postcode &&
                                !!addressTouched?.postcode
                            }
                            errorMessage={i18n.t(addressErrors?.postcode)}
                        />
                        <TextInput
                            bindName={withPrefix("city")}
                            onChange={formik.handleChange}
                            value={addressFields.city}
                            label={i18n.t("form.label.city")}
                            onBlur={formik.handleBlur}
                            error={
                                !!addressErrors?.city && !!addressTouched?.city
                            }
                            errorMessage={i18n.t(addressErrors?.city)}
                        />
                    </div>
                </>
            )}
        </>
    );
};

AddressInfoFields.propTypes = {
    addressFields: PropTypes.object,
    addressErrors: PropTypes.object,
    addressTouched: PropTypes.object,
    formik: PropTypes.object,
    prefix: PropTypes.string,
};

export default AddressInfoFields;
